/* eslint-disable max-statements */
/* eslint-disable class-methods-use-this */
import { applyActionRulesCfn } from './functions/apply-action-rules.cfn';
import { applySurroundMaterialUpdateCfn } from '../islands/functions/apply-surround-material-update.cfn';
import { applyValidationRulesCfn } from './functions/apply-validation-rules.cfn';
import { EventCrudMessage } from '../../controller-classes';
import { getCountertopFinishProfile, getSurroundFinishProfile } from '../../helpers/finish-profiles';
import { Kitchen } from '../kitchens/kitchen.class';
import { nonCoyoteDropIn } from './rules/non-coyote-drop-in/non-coyote-drop-in';
import { nonCoyoteSidePanel } from './rules/non-coyote-side-panel/non-coyote-side-panel';
import { ReferenceMap } from '../public.types';
import { requiresInsulatedJacket } from './rules/requires-insulated-jacket/requires-insulated-jacket';
import { requiresKegTap } from './rules/requires-kegtap/requires-kegtap';
import { requiresPassthrough } from './rules/passthrough/requires-passthrough';
import { requiresCountertopSeam } from './rules/requires-countertop-seam/requires-countertop-seam';
import { requiresVenting } from './rules/requires-venting/requires-venting';
import { INTERNAL_SUPPORTS_OVERRIDE_PREFIX } from './validations/internal-supports';
import { RuleScope } from './constants';

export class RulesEngine {
	#kitchen;
	#isProcessing = false;

	constructor (kitchen: Kitchen) {
		this.#kitchen = kitchen;
		this.applyActionRules = this.applyActionRules.bind(this);
		this.applyCountertopMaterialUpdate = this.applyCountertopMaterialUpdate.bind(this);
		this.applyCountertopSeamRule = this.applyCountertopSeamRule.bind(this);
		this.applyInsulatedJacketRule = this.applyInsulatedJacketRule.bind(this);
		this.applyIslandSurroundMaterialUpdate = this.applyIslandSurroundMaterialUpdate.bind(this);
		this.applyKegTapRule = this.applyKegTapRule.bind(this);
		this.applyNonCoyoteDropInRule = this.applyNonCoyoteDropInRule.bind(this);
		this.applyNonCoyoteSidePanelRule = this.applyNonCoyoteSidePanelRule.bind(this);
		this.applyPassthroughRule = this.applyPassthroughRule.bind(this);
		this.applyValidationRules = this.applyValidationRules.bind(this);
		this.applyVentingRule = this.applyVentingRule.bind(this);

		kitchen.subscribe('panel', ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.PanelItemGroup, p));
		kitchen.subscribe('panel_item', ['deleted'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe('panel_items', ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['panel_item_group'], ['moved'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.PanelItemGroup, p))
		kitchen.subscribe(['overhang'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['connection.*'], ['loaded', 'deleted', 'updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['connections'], ['created', 'loaded', 'deleted', 'updated', 'added'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['countertop_material'], ['updated', 'reset'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['island*', 'island_size'], ['created', 'loaded', 'deleted', 'updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['surround_material'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		kitchen.subscribe(['item_size', 'item_cutout'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyActionRules(RuleScope.Kitchen, p));
		// Full Height spans the cutout over the whole panel, which is one of the two documented remedies for a
		// fridge spacing warning -- without this the warning it is meant to clear stays on screen, stale.
		// Validations only: re-running action rules here would re-fire the fridge trim-kit sync, whose
		// equilibrium branch deletes the companion trim kit (eligible fridges == existing C124TK/etc. line
		// items -> else branch). Every appliance-companion action rule that *should* react to a fullHeight
		// change already has its own narrower subscription (venting, insulated jacket, kegtap, countertop
		// seam, non-coyote accessories), so dropping the action-rule half here changes nothing they care about.
		kitchen.subscribe(['panel_item_full_height'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyValidationRules(RuleScope.PanelItemGroup, p));

		// Internal supports are designer-gated (#733): re-check the toggled-off warning whenever that addon override flips.
		kitchen.subscribe(['kitchen'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => {
			const overrideKey = Array.isArray(p.payload?.new) ? p.payload.new[0] : null;
			if (typeof overrideKey === 'string' && overrideKey.startsWith(INTERNAL_SUPPORTS_OVERRIDE_PREFIX)) this.applyValidationRules(RuleScope.Island, p);
		});

		// passthrough rule fires on connection added (to create passthroughs), connection/island deleted (to clean up
		// orphaned passthroughs), and surround_material.updated (to swap PASSTHROUGH ↔ MOK-PASSTHROUGH SKUs — the two
		// render differently: standard PASSTHROUGH has no svg = rectangle, MOK-PASSTHROUGH uses gasolineCutout = circle).
		// it does NOT fire on panel_item deletions, so user-deleted passthroughs stay deleted between structural changes.
		kitchen.subscribe(['connections'], ['added', 'deleted'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyPassthroughRule(p));
		kitchen.subscribe(['island*'], ['deleted'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyPassthroughRule(p));
		kitchen.subscribe(['surround_material'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyPassthroughRule(p));

		// venting rule fires on item add (grill placed triggers vents), item SKU change (grill swapped in/out),
		// surround material change (vent SKU depends on material), and connection added (new adjacent island may
		// need vents from a neighboring MOK grill island). does NOT fire on panel_item.deleted, so user-deleted
		// vents stay deleted and grill removal does not re-add vents (same pattern as passthroughs).
		kitchen.subscribe(['panel_items'], ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyVentingRule(p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyVentingRule(p));
		kitchen.subscribe(['surround_material'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyVentingRule(p));
		kitchen.subscribe(['connections'], ['added'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyVentingRule(p));

		// countertop seam rule fires when the thing that decides a seam changes: an appliance added, swapped,
		// moved or deleted; the countertop material (granite needs a seam at any size, concrete only past 88");
		// and anything that changes the measured countertop length.
		kitchen.subscribe(['panel_items'], ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));
		kitchen.subscribe(['panel_item'], ['deleted'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));
		kitchen.subscribe(['panel_item_group'], ['moved'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));
		kitchen.subscribe(['countertop_material'], ['updated', 'reset'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));
		kitchen.subscribe(['island_size'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));
		kitchen.subscribe(['overhang'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopSeamRule(p));

		// insulated jacket rule fires on item add (grill placed triggers jacket), item SKU change (grill swapped in/out),
		// and surround material change (MOK toggle adds/removes jackets).
		// it does NOT fire on panel_item.deleted, so user-deleted jackets stay deleted and grill removal
		// does not re-add jackets (same isolation pattern as venting/passthroughs).
		kitchen.subscribe(['panel_items'], ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyInsulatedJacketRule(p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyInsulatedJacketRule(p));
		kitchen.subscribe(['surround_material'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyInsulatedJacketRule(p));

		// non-coyote drop-in / side-panel rules fire on item add (a non-Coyote appliance triggers the accessory)
		// and item SKU change (Coyote ↔ non-Coyote swap). Isolated from the general action-map sweep so
		// user-deleted accessories stay deleted -- same add-only pattern as venting / passthroughs / jackets.
		kitchen.subscribe(['panel_items'], ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyNonCoyoteDropInRule(p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyNonCoyoteDropInRule(p));
		kitchen.subscribe(['panel_items'], ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyNonCoyoteSidePanelRule(p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyNonCoyoteSidePanelRule(p));

		// kegtap rule fires on item add (kegerator placed triggers tap cutout), item SKU change
		// (kegerator swapped in/out), and panel_item.deleted (deleting the kegerator removes its
		// orphaned tap automatically -- same mandatory-companion behavior as the C124TK trim kit).
		kitchen.subscribe(['panel_items'], ['add'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyKegTapRule(p));
		kitchen.subscribe(['item_sku'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyKegTapRule(p));
		kitchen.subscribe(['panel_item'], ['deleted'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyKegTapRule(p));

		// per-island surround material reaction: snap height/depth to material-family defaults
		// (MOK ↔ standard) when the user hasn't customized, remap overhangs on family transitions,
		// then re-size panels for the new thickness. Also resets inter-island padding (0 ↔ 1).
		kitchen.subscribe(['surround_material'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyIslandSurroundMaterialUpdate(p));

		// countertop material reaction: swap countertop thickness (0.5 ↔ 1.25) on MOK ↔ non-MOK
		// countertop transitions. Keyed off the countertop material key prefix (moks|...), not
		// the surround material — so switching surround alone does not stomp countertop thickness.
		kitchen.subscribe(['countertop_material'], ['updated'], (p:EventCrudMessage<unknown, ReferenceMap>) => this.applyCountertopMaterialUpdate(p));
	}

	applyActionRules (ruleScope: number, params: EventCrudMessage<unknown, ReferenceMap>): void {
		// suppress re-entrant calls -- rules that modify the tree (delete/add vents, passthroughs)
		// fire events that would trigger rules again, causing corruption during iteration
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) applyActionRulesCfn(this, ruleScope, params.payload);
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the validation half of the rule sweep without touching action rules.
	 * Used by `panel_item_full_height.updated`, whose intent is to clear/refresh the
	 * fridge-spacing warning without re-firing the fridge trim-kit action rule --
	 * that rule's equilibrium branch deletes the companion trim kit the moment
	 * `eligibleItems` and existing `C124TK`/`CBIR24TK`/`C121TK`/`CBIR21TK`/`CRTA21TK`/`C115TK`
	 * line-item counts line up (see issue #618).
	 */
	applyValidationRules (ruleScope: number, params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) applyValidationRulesCfn(ruleScope, params.payload);
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the passthrough rule in isolation -- only called on connection/island structural changes (added, deleted).
	 * Separated from the general rule sweep so passthroughs are not re-added after user deletion.
	 */
	applyPassthroughRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) requiresPassthrough({ refMap: params.payload.ref });
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the insulated jacket rule in isolation -- called on item add, SKU change, and surround material change.
	 * Adds insulated jackets to grills when the island uses MOK surround material; removes them when material changes away from MOK.
	 * Does NOT fire on panel_item.deleted, so user-deleted jackets stay deleted (same isolation pattern as venting/passthroughs).
	 */
	applyInsulatedJacketRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) requiresInsulatedJacket({ refMap: params.payload.ref as ReferenceMap });
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the kegtap rule -- called on item add, SKU change, and panel_item.deleted.
	 * Adds a rule-managed C124KEG-TAP countertop cutout to the same PanelItemGroup as a C124KEG
	 * kegerator; removes rule-managed taps that are no longer paired with a kegerator (kegerator
	 * swapped out or deleted). The tap is a mandatory companion of the kegerator, mirroring the
	 * C124TK trim kit: deleting the kegerator removes the tap, and deleting the tap alone re-adds it.
	 */
	applyKegTapRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) requiresKegTap({ refMap: params.payload.ref as ReferenceMap });
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the non-Coyote drop-in rule in isolation -- called on item add and item SKU change.
	 * Adds a CUSTOM_NONCOY_DROPIN accessory per non-Coyote drop-in (front-and-top placement) item.
	 * Uses the same add-only helper (`syncAccessorySkuCount`) but isolated from the general
	 * action-map sweep so user-deleted accessories stay deleted (same pattern as venting/passthrough).
	 */
	applyNonCoyoteDropInRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) nonCoyoteDropIn({ refMap: params.payload.ref as ReferenceMap });
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the non-Coyote side-panel rule in isolation -- called on item add and item SKU change.
	 * Adds a CUSTOM_NONCOY_SIDEPANEL accessory per non-Coyote front-only item (excluding cutouts,
	 * vents, passthroughs, system, outlet). Isolated from the general action-map sweep so
	 * user-deleted accessories stay deleted (same pattern as venting/passthrough).
	 */
	applyNonCoyoteSidePanelRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) nonCoyoteSidePanel({ refMap: params.payload.ref as ReferenceMap });
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Reacts to a surround material change -- kitchen-level default or a single island's own
	 * override; `getReferenceMap` cannot resolve a `SurroundMaterial` id (it isn't a tracked
	 * entity), so this always collapses back to the kitchen's refMap and cannot identify which
	 * instance actually fired the event. Instead of assuming it was the kitchen default, every
	 * island is checked independently via its OWN `surround` (its override, or the shared kitchen
	 * instance when it has none): an island with no override aliases the same SurroundMaterial as
	 * the kitchen, so a kitchen-level change is naturally visible to it; an island overridden away
	 * from the kitchen default sees no transition on its own (unrelated) instance and its padding
	 * is correctly left alone either way. On any cross-family transition (looked up via
	 * `finishProfiles`), applies the target profile's defaults for inter-island padding, per-panel
	 * overhangs, and Grill/Bar height/depth -- but only when the current value matches a known
	 * preset (customizations preserved). Same-family changes only re-size panels for the new
	 * per-material thickness. Countertop thickness is handled separately via
	 * `applyCountertopMaterialUpdate` because it's tied to the countertop material, not the
	 * surround. Skips the #isProcessing guard so cascading island_size.updated events still reach
	 * applyActionRules for kitchen-scope rule re-evaluation.
	 */
	applyIslandSurroundMaterialUpdate (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (typeof params.payload.ref === 'string') {
			const { id, refMap } = this.#kitchen;
			params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
		}
		const ref = params.payload.ref as ReferenceMap;
		const kitchen = ref?.kitchen ?? this.#kitchen;
		const islands = ref?.kitchen?.design?.islands ?? ref?.islands ?? kitchen?.design?.islands ?? null;
		if (!islands) return;

		islands.children.forEach((island) => {
			const surround = island.surround;
			const prevProfile = getSurroundFinishProfile(surround?.previousMaterial);
			const nextProfile = getSurroundFinishProfile(surround?.material);
			const isFamilyTransition = !!prevProfile && !!nextProfile && prevProfile !== nextProfile;
			if (!isFamilyTransition || !prevProfile || !nextProfile) return;

			island.connections.children.forEach((connection) => {
				if (connection.padding === prevProfile.padding) connection.padding = nextProfile.padding;
			});
		});

		islands.children.forEach((island) => applySurroundMaterialUpdateCfn(island));
	}

	/**
	 * Reacts to a countertop material change by snapping countertop thickness to the
	 * target countertop profile's default (currently: standard 1.25", MOK 0.5"). Only
	 * fires on a family transition and only if the current thickness matches the
	 * outgoing profile's default — so custom thicknesses are preserved. Kept separate
	 * from the surround handler so switching surround alone does not stomp thickness.
	 * Skips the #isProcessing guard for the same reason applyIslandSurroundMaterialUpdate does.
	 */
	applyCountertopMaterialUpdate (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (typeof params.payload.ref === 'string') {
			const { id, refMap } = this.#kitchen;
			params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
		}
		const ref = params.payload.ref as ReferenceMap;
		const kitchen = ref?.kitchen ?? this.#kitchen;
		const countertop = kitchen?.material?.countertop;
		if (!countertop) return;

		const prevProfile = getCountertopFinishProfile(countertop.previousKey);
		const nextProfile = getCountertopFinishProfile(countertop.key);
		if (!prevProfile || !nextProfile || prevProfile === nextProfile) return;
		if (countertop.thickness === prevProfile.thickness) countertop.thickness = nextProfile.thickness;
	}

	/**
	 * Runs the venting rule in isolation -- called on item add, SKU change, and surround material change.
	 * Does NOT fire on panel_item.deleted, so user-deleted vents stay deleted and grill removal
	 * does not re-add vents (same isolation pattern as passthroughs).
	 */
	applyVentingRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) requiresVenting({ refMap: params.payload.ref as ReferenceMap });
		} finally {
			this.#isProcessing = false;
		}
	}

	/**
	 * Runs the countertop seam rule in isolation -- called on appliance add/delete/swap/move, countertop
	 * material change, and anything that changes the measured countertop length. Places the seams the
	 * material rules require and prunes rule-generated seams the design has outgrown. Hand-placed
	 * breakpoints are never moved or removed.
	 * @param params the event carrying the reference map to run against
	 */
	applyCountertopSeamRule (params: EventCrudMessage<unknown, ReferenceMap>): void {
		if (this.#isProcessing) return;

		this.#isProcessing = true;
		try {
			if (typeof params.payload.ref === 'string') {
				const { id, refMap } = this.#kitchen;
				params.payload.ref = id === params.payload.ref ? refMap : (this.#kitchen.getReferenceMap(params.payload.ref) ?? refMap);
			}
			if (params.payload.ref) requiresCountertopSeam({ refMap: params.payload.ref as ReferenceMap });
		} finally {
			this.#isProcessing = false;
		}
	}
}
